import type { FC, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import FormatListNumberedRounded from '@mui/icons-material/FormatListNumberedRounded';
import { Box, Grid, Stack, Typography } from '@mui/material';

import ActionPlanStatisticsIcon from 'src/assets/action-plan/static-icon2.png';
import type { ActionPlanStatistics } from 'src/types/planAction';
import ButtonModal from 'src/components/common/ButtonModal';
import PercentageDoughnut from 'src/components/planAction/PlanActionStatisticsPage/components/PercentageDoughnut';

import ActionPlanStatusDoughnut from './components/ActionPlanStatusDoughnut';
import { HeadcountCoverage } from './components/HeadcountCoverage';
import NumberDisplay from './components/NumberDisplay';
import OrgUnitCoverage from './components/OrgUnitCoverage';

import { styles } from './index.styles';

interface ActionPlanCoverageProps {
  statistics: ActionPlanStatistics;
  passedRef: RefObject<any>;
  bigText: boolean;
}

const ActionPlanCoverage: FC<ActionPlanCoverageProps> = ({
  statistics,
  passedRef,
  bigText,
}) => {
  const { t } = useTranslation('planAction');

  return (
    <Grid
      container
      spacing={1}
      data-test-id="coverage-statistics"
      ref={passedRef}
    >
      <Grid item xs={6}>
        <Box sx={styles.tile}>
          <OrgUnitCoverage
            {...statistics}
            fractionLabel={t('Statistics.Coverage.OrgUnitCoverageLabel')}
            bigText={bigText}
          />
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box sx={styles.tile}>
          <HeadcountCoverage
            {...statistics}
            fractionLabel={t('Statistics.Coverage.HeadcountCoverageLabel')}
            bigText={bigText}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box sx={styles.tile}>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: bigText ? 30 : 20,
              }}
            >
              {t('Statistics.Coverage.ActionPlans')}
            </Typography>
            <ButtonModal
              headline={t('Statistics.Coverage.ActionPlans')}
              text={t('Statistics.Coverage.ActionPlansHelp')}
            />
          </Stack>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid item xs={3}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Box sx={styles.imageWrapper}>
                  <img
                    src={ActionPlanStatisticsIcon}
                    alt="ActionPlanStatistics Icon"
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack
                direction="column"
                spacing={4}
                sx={{
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <NumberDisplay
                  label={t('Statistics.Coverage.ActionPlansCreated')}
                  number={statistics.actionPlanCount}
                  bigText={bigText}
                />
                <NumberDisplay
                  label={t('Statistics.Coverage.ActionPlansOverDueDate')}
                  number={statistics.actionPlansOverDueDate}
                  bigText={bigText}
                />
              </Stack>
            </Grid>
            <Grid item xs={5}>
              <ActionPlanStatusDoughnut {...statistics} bigText={bigText} />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box sx={styles.tile}>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: bigText ? 30 : 20,
              }}
            >
              {t('Statistics.Coverage.SingleActions')}
            </Typography>
            <ButtonModal
              headline={t('Statistics.Coverage.SingleActions')}
              text={t('Statistics.Coverage.SingleActionsHelp')}
            />
          </Stack>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid item xs={3} direction="column">
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Box sx={styles.imageWrapper}>
                  <FormatListNumberedRounded color="secondary" />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack
                direction="column"
                sx={{
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <NumberDisplay
                  label={t('Statistics.Coverage.SingleActionsRecorded')}
                  number={statistics.actionCount}
                  bigText={bigText}
                />
              </Stack>
            </Grid>
            <Grid item xs={5}>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  pl: 6,
                }}
              >
                <PercentageDoughnut
                  nominator={statistics.actionProgressSum}
                  denominator={
                    statistics.actionCount -
                    statistics.actionNotInImplementationSum -
                    statistics.actionsDiscontinued
                  }
                  showNA={
                    statistics.actionCount ===
                    statistics.actionNotInImplementationSum +
                      statistics.actionsDiscontinued
                  }
                />
                <Typography
                  sx={{
                    maxWidth: '10rem',
                    textAlign: 'center',
                    ml: 4,
                  }}
                >
                  {t('Statistics.Coverage.SingleActionsImplementationRate')}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ActionPlanCoverage;
